import React, { useEffect, useRef, useState } from 'react';
import {
  FaRobot, FaSync, FaUsers, FaUserPlus, FaMoneyBill, FaClock,
  FaExclamationTriangle, FaSpinner, FaPaperPlane, FaTrash, FaMagic,
  FaChartLine, FaHeart, FaCheckCircle, FaChevronRight, FaBolt,
  FaRegLightbulb, FaCommentDots, FaUserAstronaut, FaChevronLeft,
  FaMapMarkerAlt, FaGraduationCap, FaBriefcase, FaRing, FaAward
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { aiApi } from '../../services/ai';
import AICreateUserModal from './AICreateUserModal';
import ResponseRenderer from '../chat/ResponseRenderer';
import { imageUrl } from '../../utils/imageUrl';

const AIAdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState('');
  const [sending, setSending] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);

  // Matchmaking state
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchIndex, setMatchIndex] = useState(0);

  const messagesRef = useRef(null);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dash, hist] = await Promise.all([
        aiApi.getDashboard(),
        aiApi.getChatHistory(),
      ]);
      if (dash.success) setData(dash.data);
      else throw new Error(dash.message || 'Failed to load');
      if (hist.success) {
        setMessages((hist.data || []).map(h => ({ role: h.role, content: h.content })));
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
      toast.error('Error loading AI dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadMatches = async () => {
    setMatchesLoading(true);
    try {
      const res = await aiApi.getTodayMatches();
      if (res.success) {
        setMatches(res.data.matches || []);
        setMatchIndex(0);
      }
    } catch (e) {
      console.error('Matches load failed:', e);
    } finally {
      setMatchesLoading(false);
    }
  };

  useEffect(() => {
    load().then(loadMatches);
  }, []);

  useEffect(() => {
    messagesRef.current?.scrollTo(0, messagesRef.current.scrollHeight);
  }, [messages, streaming]);

  const ask = async (text) => {
    if (!text.trim() || sending) return;
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setStreaming('');

    let full = '';
    let render = null;

    try {
      await aiApi.chatStream(text, {
        onToken: (t) => { full += t; setStreaming(full); },
        onRender: (r) => { render = r; },
        onDone: () => {
          setMessages(prev => [...prev, { role: 'assistant', content: full, render }]);
          setStreaming('');
          setSending(false);
        },
      });
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ ' + e.message }]);
      setStreaming('');
      setSending(false);
      toast.error('AI chat error: ' + e.message);
    }
  };

  const resolveAlert = async (id) => {
    try {
      const r = await aiApi.resolveAlert(id);
      if (!r.success) throw new Error(r.message);
      setData(prev => ({
        ...prev,
        alerts: prev.alerts.filter(a => a.id !== id),
        stats: { ...prev.stats, open_alerts: Math.max(0, prev.stats.open_alerts - 1) },
      }));
      toast.success('Alert resolved');
    } catch (e) { toast.error(e.message); }
  };

  const clearChat = async () => {
    if (!window.confirm('Clear chat history?')) return;
    try {
      await aiApi.clearChat();
      setMessages([]);
      toast.success('Chat cleared');
    } catch (e) { toast.error(e.message); }
  };

  /* ---------- LOADING ---------- */
  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-96">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-indigo-500 opacity-20 animate-ping"></div>
          <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
            <FaRobot className="text-white text-3xl animate-pulse" />
          </div>
        </div>
        <p className="mt-6 text-gray-600 font-medium">NepalxAI is thinking…</p>
        <p className="text-xs text-gray-400 mt-1">Loading your dashboard</p>
      </div>
    );
  }

  /* ---------- ERROR ---------- */
  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-8 text-center shadow-lg">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
            <FaExclamationTriangle className="text-red-600 text-2xl" />
          </div>
          <p className="text-red-800 font-semibold mb-1">Something went wrong</p>
          <p className="text-red-600 text-sm mb-5">{error}</p>
          <button
            onClick={load}
            className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium shadow-md hover:shadow-lg transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;
  const s = data.stats;

  const statCards = [
    { title: 'Total Users',      value: s.total_users,      icon: FaUsers,     gradient: 'from-blue-500 to-blue-600',       bg: 'bg-blue-50',    text: 'text-blue-600' },
    { title: 'New This Month',   value: s.new_this_month,   icon: FaUserPlus,  gradient: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-50', text: 'text-emerald-600' },
    { title: 'Paid Users',       value: s.paid_users,       icon: FaMoneyBill, gradient: 'from-purple-500 to-purple-600',   bg: 'bg-purple-50',  text: 'text-purple-600' },
    { title: 'Pending Payments', value: s.pending_payments, icon: FaClock,     gradient: 'from-amber-500 to-amber-600',     bg: 'bg-amber-50',   text: 'text-amber-600' },
  ];

  const current = matches[matchIndex];

  return (
    <div className="max-w-7xl mx-auto">
      {/* ============ HERO HEADER ============ */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-8 mb-6 shadow-xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full -ml-10 -mb-10"></div>

        <div className="relative flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <FaRobot className="text-white text-3xl" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-white">NepalxAI</h1>
                <span className="px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                  for Bihebari
                </span>
              </div>
              <p className="text-white/80 text-sm mt-1 flex items-center gap-2">
                <FaBolt className="text-yellow-300" />
                Smart matchmaking · Updated {new Date(data.generatedAt).toLocaleTimeString()}
              </p>
            </div>
          </div>

          <button
            onClick={() => { load(); loadMatches(); }}
            disabled={loading}
            className="group flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-xl font-medium border border-white/30 transition-all"
          >
            <FaSync className={`${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ============ AI MATCHMAKING HERO ============ */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-6">
        {/* Header bar */}
        <div className="bg-gradient-to-r from-pink-500 via-rose-500 to-orange-500 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-md">
              <FaHeart className="text-white text-lg" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                Today's Smart Matches
                <span className="px-2 py-0.5 bg-white/25 backdrop-blur-sm text-white text-[10px] font-bold rounded-full uppercase tracking-wide">
                  AI
                </span>
              </h2>
              <p className="text-white/85 text-xs mt-0.5">
                Deep comparison across all profile tables
              </p>
            </div>
          </div>

          {matches.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-white/25 backdrop-blur-sm text-white rounded-full text-xs font-semibold">
                {matches.length} match{matches.length === 1 ? '' : 'es'}
              </span>
              <button
                onClick={loadMatches}
                disabled={matchesLoading}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition"
                title="Refresh matches"
              >
                <FaSync className={matchesLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        {matchesLoading ? (
          <div className="p-16 text-center">
            <FaSpinner className="text-4xl text-pink-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 text-sm">NepalxAI is finding the best matches…</p>
            <p className="text-xs text-gray-400 mt-1">Comparing preferences across all tables</p>
          </div>
        ) : matches.length === 0 ? (
          <EmptyMatches />
        ) : (
          <div className="relative">
            {/* Slider */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-9 gap-4 items-center">
                {/* LEFT — Matcher */}
                <div className="lg:col-span-4">
                  <MatchProfileCard
                    user={current.matcher}
                    role="Matcher"
                    accent="indigo"
                  />
                </div>

                {/* CENTER — Score */}
                <div className="lg:col-span-1 flex flex-col items-center justify-center py-4 lg:py-0">
                  <MatchScoreRing score={current.score} />
                  <p className="mt-3 text-[11px] font-bold uppercase tracking-wide text-gray-400 text-center">
                    Match Score
                  </p>
                </div>

                {/* RIGHT — Matched */}
                <div className="lg:col-span-4">
                  <MatchProfileCard
                    user={current.matched}
                    role="Best Match"
                    accent="pink"
                  />
                </div>
              </div>

              {/* Reasons */}
              {current.reasons && current.reasons.length > 0 && (
                <div className="mt-6 bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-100 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-pink-100 flex items-center justify-center">
                      <FaAward className="text-pink-600" />
                    </div>
                    <p className="text-sm font-bold text-gray-800">Why this is a great match</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {current.reasons.map((r, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 bg-white/70 backdrop-blur-sm rounded-lg px-3 py-2 border border-pink-100"
                      >
                        <FaCheckCircle className="text-green-500 mt-0.5 flex-shrink-0 text-xs" />
                        <span className="text-xs text-gray-700">{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Slider controls */}
            <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-between bg-gray-50">
              <button
                onClick={() => setMatchIndex(i => Math.max(0, i - 1))}
                disabled={matchIndex === 0}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <FaChevronLeft className="text-xs" />
                Previous
              </button>

              {/* Dots */}
              <div className="flex items-center gap-1.5">
                {matches.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setMatchIndex(i)}
                    className={`transition-all rounded-full ${
                      i === matchIndex
                        ? 'w-6 h-2 bg-gradient-to-r from-pink-500 to-rose-500'
                        : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Match ${i + 1}`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-mono">
                  {matchIndex + 1} / {matches.length}
                </span>
                <button
                  onClick={() => setMatchIndex(i => Math.min(matches.length - 1, i + 1))}
                  disabled={matchIndex === matches.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white rounded-xl text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============ STAT CARDS ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <div
              key={i}
              className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-transparent hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`${card.bg} ${card.text} w-12 h-12 rounded-xl flex items-center justify-center`}>
                  <Icon className="text-xl" />
                </div>
                <FaChevronRight className="text-gray-300 group-hover:text-gray-400 group-hover:translate-x-1 transition-all" />
              </div>
              <p className="text-gray-500 text-sm font-medium">{card.title}</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{Number(card.value).toLocaleString()}</p>
              <div className={`h-1 mt-4 rounded-full bg-gradient-to-r ${card.gradient} opacity-70 group-hover:opacity-100 transition-opacity`}></div>
            </div>
          );
        })}
      </div>

      {/* ============ AI SUMMARY ============ */}
      <div className="relative bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100 overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
              <FaChartLine className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Daily Summary</h2>
              <p className="text-xs text-gray-400">AI-generated analysis</p>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-[15px]">
            {data.summary}
          </p>
        </div>
      </div>

      {/* ============ ALERTS ============ */}
      {data.alerts.length > 0 && (
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-md">
              <FaExclamationTriangle className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Open Alerts</h2>
              <p className="text-xs text-gray-400">{data.alerts.length} item{data.alerts.length === 1 ? '' : 's'} requiring attention</p>
            </div>
          </div>
          <ul className="space-y-2">
            {data.alerts.map(a => (
              <li
                key={a.id}
                className="group flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  a.severity === 'high' ? 'bg-red-500 shadow-red-200 shadow-md' :
                  a.severity === 'medium' ? 'bg-yellow-500 shadow-yellow-200 shadow-md' :
                  'bg-green-500 shadow-green-200 shadow-md'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-gray-800 font-medium truncate">{a.message}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    <span className="inline-block px-2 py-0.5 bg-gray-100 rounded-md font-mono">{a.type}</span>
                    <span className="mx-2">·</span>
                    {new Date(a.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => resolveAlert(a.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity px-4 py-1.5 bg-gray-900 hover:bg-black text-white text-xs font-medium rounded-lg"
                >
                  Resolve
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ============ CREATE USER CTA ============ */}
      <div className="relative overflow-hidden bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 mb-6 shadow-xl">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
        <div className="absolute bottom-0 left-10 w-24 h-24 bg-white/10 rounded-full -mb-12"></div>

        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <FaUserAstronaut className="text-white text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                AI Create User
                <span className="px-2 py-0.5 bg-yellow-300/90 text-yellow-900 text-[10px] font-bold rounded-full">
                  FAST
                </span>
              </h2>
              <p className="text-white/80 text-sm mt-0.5">
                Describe a user in plain language — AI fills every field in 1-2 seconds.
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowCreateUser(true)}
            className="group flex items-center gap-2 px-6 py-3 bg-white text-indigo-600 font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all hover:scale-105"
          >
            <FaMagic className="group-hover:rotate-12 transition-transform" />
            Create User
            <FaChevronRight className="text-xs group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* ============ CHAT ============ */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
              <FaCommentDots className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Ask AI</h2>
              <p className="text-xs text-gray-400">Query users, payments, activity</p>
            </div>
          </div>

          {messages.length > 0 && (
            <button
              onClick={clearChat}
              className="group flex items-center gap-2 text-sm px-3 py-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FaTrash className="text-xs group-hover:scale-110 transition-transform" />
              Clear
            </button>
          )}
        </div>

        <div className="p-6">
          {messages.length === 0 && !streaming && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <FaRegLightbulb className="text-amber-500" />
                <p className="text-sm font-medium text-gray-700">Try asking</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {data.suggestedPrompts.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => ask(p)}
                    disabled={sending}
                    className="group text-left px-4 py-3 bg-gradient-to-r from-gray-50 to-white hover:from-indigo-50 hover:to-purple-50 border border-gray-200 hover:border-indigo-200 rounded-xl text-sm text-gray-700 hover:text-indigo-700 transition-all flex items-center justify-between"
                  >
                    <span>{p}</span>
                    <FaChevronRight className="text-gray-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all text-xs" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {(messages.length > 0 || streaming) && (
            <div ref={messagesRef} className="max-h-[32rem] overflow-y-auto mb-5 pr-2 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {m.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md">
                      <FaRobot className="text-white text-xs" />
                    </div>
                  )}
                  <div className="max-w-[85%]">
                    <div className={`px-4 py-2.5 rounded-2xl whitespace-pre-wrap text-sm leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-br-sm shadow-md'
                        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                    }`}>
                      {m.content}
                    </div>
                    {m.role === 'assistant' && m.render && (
                      <ResponseRenderer render={m.render} />
                    )}
                  </div>
                </div>
              ))}
              {streaming && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md">
                    <FaRobot className="text-white text-xs animate-pulse" />
                  </div>
                  <div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-bl-sm bg-gray-100 text-gray-800 text-sm whitespace-pre-wrap leading-relaxed">
                    {streaming}
                    <span className="inline-block w-1.5 h-4 bg-indigo-500 ml-1 animate-pulse align-middle rounded-sm"></span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 items-end">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    ask(input);
                  }
                }}
                placeholder="Ask about users, payments, activity…"
                rows={2}
                disabled={sending}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-sm transition-all"
              />
              <span className="absolute bottom-3 right-3 text-xs text-gray-400 pointer-events-none">
                ⏎ to send
              </span>
            </div>
            <button
              onClick={() => ask(input)}
              disabled={sending || !input.trim()}
              className="h-[52px] px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 text-white rounded-xl font-semibold flex items-center gap-2 shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span className="hidden sm:inline">Sending</span>
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  <span className="hidden sm:inline">Send</span>
                </>
              )}
            </button>
          </div>

          <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <FaCheckCircle className="text-green-500" />
              Read-only · Safe
            </span>
            <span>·</span>
            <span>Powered by NepalxAI</span>
          </div>
        </div>
      </div>

      {showCreateUser && (
        <AICreateUserModal
          onClose={() => setShowCreateUser(false)}
          onCreated={() => {
            setShowCreateUser(false);
            load();
            loadMatches();
          }}
        />
      )}
    </div>
  );
};

/* ============ MATCH PROFILE CARD ============ */
/* ============ MATCH PROFILE CARD ============ */
function MatchProfileCard({ user, role, accent = 'indigo' }) {
  // ⚠️ Hooks FIRST (must run on every render)
  const [imgError, setImgError] = useState(false);

  if (!user) return null;

  const initial = (user.name || 'U').charAt(0).toUpperCase();
  const img = imageUrl(user.image);

  const styles = {
    indigo: {
      grad: 'from-indigo-500 to-blue-600',
      ring: 'ring-indigo-200',
      chip: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    },
    pink: {
      grad: 'from-pink-500 to-rose-600',
      ring: 'ring-pink-200',
      chip: 'bg-pink-50 text-pink-700 border-pink-100',
    },
  };
  const st = styles[accent] || styles.indigo;

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden ring-1 ${st.ring}`}>
      <div className={`px-4 py-2 bg-gradient-to-r ${st.grad} text-white text-[11px] font-bold uppercase tracking-wider flex items-center justify-between`}>
        <span>{role}</span>
        <span className="opacity-75 font-mono">#{user.id}</span>
      </div>

      <div className="p-5 flex items-center gap-4">
        {img && !imgError ? (
          <img
            src={img}
            alt={user.name}
            onError={() => setImgError(true)}
            className={`w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-lg ring-2 ${st.ring}`}
          />
        ) : (
          <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${st.grad} flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-lg`}>
            {initial}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-lg truncate">{user.name || '—'}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {user.age && (
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.chip} font-medium`}>
                {user.age} yrs
              </span>
            )}
            {user.gender && (
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.chip} font-medium capitalize`}>
                {user.gender}
              </span>
            )}
            {user.marital && (
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.chip} font-medium capitalize`}>
                {user.marital}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 pb-5 space-y-2">
        <DetailRow icon={FaMapMarkerAlt} label="Location" value={[user.city, user.state].filter(Boolean).join(', ')} />
        <DetailRow icon={FaGraduationCap} label="Education" value={user.education} />
        <DetailRow icon={FaBriefcase} label="Occupation" value={user.occupation} />
        <DetailRow icon={FaRing} label="Looking For" value={user.lookingFor || user.lifePartner?.lookingFor} />
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className="text-gray-300 flex-shrink-0" />
      <span className="text-gray-400">{label}:</span>
      <span className="text-gray-800 font-medium truncate">{value}</span>
    </div>
  );
}

/* ============ SCORE RING ============ */
function MatchScoreRing({ score = 0 }) {
  const clamped = Math.max(0, Math.min(100, score));
  const size = 88;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;

  const color = clamped >= 80 ? '#10b981' : clamped >= 60 ? '#f59e0b' : '#ef4444';
  const label = clamped >= 80 ? 'PERFECT' : clamped >= 60 ? 'GREAT' : 'GOOD';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold text-gray-800 leading-none">{clamped}</span>
        <span className="text-[9px] font-bold uppercase mt-0.5" style={{ color }}>
          {label}
        </span>
      </div>
    </div>
  );
}

/* ============ EMPTY MATCHES ============ */
function EmptyMatches() {
  return (
    <div className="p-12 text-center">
      <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center mb-4">
        <FaHeart className="text-pink-400 text-2xl" />
      </div>
      <p className="text-gray-700 font-semibold mb-1">No matches found yet</p>
      <p className="text-sm text-gray-500 max-w-md mx-auto">
        NepalxAI scans new registrations and generates fresh match pairs every 30 minutes. Check back soon.
      </p>
    </div>
  );
}

export default AIAdminDashboard;